import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import orderService from '../../services/orderService';
import Loading from '../../components/Common/Loading';
import OrderStatusBadge from '../../components/Orders/OrderStatusBadge';
import Button from '../../components/Common/Button';
import Modal from '../../components/Common/Modal';

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, isRtl } = useLocalization();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');

  useEffect(() => {
    loadOrderDetails();
  }, [id]);

  const loadOrderDetails = async () => {
    try {
      setIsLoading(true);
      const data = await orderService.getById(id);
      setOrder(data);
    } catch (error) {
      console.error('Error loading order details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      await orderService.updateStatus(id, newStatus);
      setShowStatusModal(false);
      loadOrderDetails();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const parseOrderDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') {
      const ms = value < 1e12 ? value * 1000 : value;
      const date = new Date(ms);
      return isNaN(date.getTime()) ? null : date;
    }
    const raw = value.toString().trim();
    if (!raw) return null;
    const dotNetMatch = raw.match(/Date\((\d+)\)/i);
    if (dotNetMatch) {
      const ms = Number(dotNetMatch[1]);
      const date = new Date(ms);
      return isNaN(date.getTime()) ? null : date;
    }
    if (/^\d+$/.test(raw)) {
      const num = Number(raw);
      const ms = num < 1e12 ? num * 1000 : num;
      const date = new Date(ms);
      return isNaN(date.getTime()) ? null : date;
    }
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const getOrderItemDisplayName = (item) => {
    const productName = isRtl
      ? (item.productNameAr || item.productNameEn)
      : (item.productNameEn || item.productNameAr);
    const brandName = isRtl
      ? (item.brandNameAr || item.brandNameEn || item.brandAr || item.brandEn)
      : (item.brandNameEn || item.brandNameAr || item.brandEn || item.brandAr);

    return { brandName, productName };
  };

  const getSourceLabel = (source) => {
    const labels = {
      0: isRtl ? 'التطبيق' : 'App',
      1: isRtl ? 'الموقع' : 'Web',
    };
    return labels[source] ?? (isRtl ? 'غير معروف' : 'Unknown');
  };

  const getWhatsAppUrl = (phone) => {
    if (!phone) return null;

    let digits = phone.toString().replace(/\D/g, '');
    if (digits.startsWith('00')) digits = digits.slice(2);
    if (digits.startsWith('0')) digits = `964${digits.slice(1)}`;
    else if (!digits.startsWith('964')) digits = `964${digits}`;

    return `https://wa.me/${digits}`;
  };

  if (isLoading) {
    return <Loading text="Loading order details..." />;
  }

  if (!order) {
    return <div>{isRtl ? 'الطلب غير موجود' : 'Order not found'}</div>;
  }

  return (
    <div className="order-details" dir={isRtl ? 'rtl' : 'ltr'}>
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'تفاصيل الطلب' : 'Order Details'}</h1>
            <p className="page-description">#{order.orderNumber}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="primary" onClick={() => setShowStatusModal(true)}>
              {isRtl ? 'تحديث الحالة' : 'Update Status'}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2" style={{ gap: 'var(--sp-5)' }}>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'معلومات الطلب' : 'Order Information'}</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">{isRtl ? 'رقم الطلب' : 'Order Number'}</label>
              <div>{order.orderNumber}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'الحالة' : 'Status'}</label>
              <div>
                <OrderStatusBadge
                  status={order.status}
                  statusKey={order.statusEn || order.statusAr || order.status}
                  statusLabel={isRtl ? (order.statusAr || order.statusEn || order.status) : (order.statusEn || order.statusAr || order.status)}
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'المصدر' : 'Source'}</label>
              <div>{getSourceLabel(order.source)}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'التاريخ' : 'Date'}</label>
              <div>
                {(() => {
                  const date = parseOrderDate(order.createdAtUtc || order.createdAt);
                  if (!date) return isRtl ? 'غير معروف' : 'Unknown';
                  return date.toLocaleString(isRtl ? 'ar-SA' : 'en-US');
                })()}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'الإجمالي' : 'Total'}</label>
              <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 700 }}>{order.total} IQD</div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'المجموع الفرعي' : 'Subtotal'}</label>
              <div>{order.subtotal} IQD</div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'التوصيل' : 'Shipping'}</label>
              <div>{order.shippingCost} IQD</div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'معلومات العميل' : 'Customer Information'}</h3>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label className="form-label">{isRtl ? 'الاسم' : 'Name'}</label>
              <div>{order.customerName}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'الهاتف' : 'Phone'}</label>
              <div>
                {getWhatsAppUrl(order.phone) ? (
                  <a
                    href={getWhatsAppUrl(order.phone)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                    title={isRtl ? 'فتح في واتساب' : 'Open in WhatsApp'}
                  >
                    {order.phone}
                  </a>
                ) : '-'}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'العنوان' : 'Address'}</label>
              <div>{order.address}</div>
            </div>
            <div className="form-group">
              <label className="form-label">{isRtl ? 'ملاحظات' : 'Notes'}</label>
              <div>{order.notes || '-'}</div>
            </div>
          </div>
        </div>

        <div className="card card-full-width">
          <div className="card-header">
            <h3 className="card-title">{isRtl ? 'عناصر الطلب' : 'Order Items'}</h3>
          </div>
          <div className="card-body">
            <table className="table">
              <thead>
                <tr>
                  <th>{isRtl ? 'المنتج' : 'Product'}</th>
                  <th>{isRtl ? 'الكمية' : 'Quantity'}</th>
                  <th>{isRtl ? 'السعر' : 'Price'}</th>
                  <th>{isRtl ? 'الإجمالي' : 'Total'}</th>
                </tr>
              </thead>
              <tbody>
                {order.items?.map((item, index) => {
                  const { brandName, productName } = getOrderItemDisplayName(item);

                  return (
                    <tr key={index}>
                      <td>
                        {brandName ? (
                          <>
                            <strong>{brandName}</strong>
                            {' - '}
                          </>
                        ) : null}
                        {productName}
                      </td>
                      <td>{item.quantity}</td>
                      <td>{item.unitPrice} IQD</td>
                      <td>{item.lineTotal ?? item.quantity * item.unitPrice} IQD</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        isOpen={showStatusModal}
        onClose={() => setShowStatusModal(false)}
        title={isRtl ? 'تحديث حالة الطلب' : 'Update Order Status'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
              {get('cancel')}
            </Button>
            <Button variant="primary" onClick={handleStatusUpdate}>
              {get('save')}
            </Button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الحالة الجديدة' : 'New Status'}</label>
          <select
            className="form-select"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
          >
            <option value="">{isRtl ? 'اختر الحالة' : 'Select Status'}</option>
            <option value="pending">{get('pending')}</option>
            <option value="packed">{get('packed')}</option>
            <option value="shipped">{get('shipped')}</option>
            <option value="returned">{isRtl ? 'مرتجع' : 'Returned'}</option>
            <option value="canceled">{isRtl ? 'ملغي' : 'Canceled'}</option>
          </select>
        </div>
      </Modal>
    </div>
  );
};

export default OrderDetails;
